import { ColorTypes } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { NodeSource } from "@omegagrid/tree";
import { CellModel, ColumnSource, RowSource } from "@omegagrid/grid";
import { ToolbarItem, ToolbarDropdown } from "@omegagrid/toolbar";
import rp, { GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";
import { DimTreeAdapter } from '../../adapters';
import { DimensionDefinition, StatementRow, StatementDefinition, StatementRowFromRow } from "@reporting/rp-types/definition";
import { MODULE_CODES } from "@reporting/rp-types/accounting";
import { ActionEvent } from "@omegagrid/core/dist/common/actions";

export class FinAggregateGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private _loading: Promise<void>;
	private aggregation: StatementRowFromRow;
	private idStatementFrom: number = null;

	constructor(
		private idRow: number,
	) { super() }

	/**
	 * Loads the aggregation once per adapter instance. getOptions() (called by both the editor and the grid)
	 * and getRows() all share the same promise, so a later call cannot overwrite a report/row
	 * the user has already picked in the toolbar while the grid was still loading.
	 */
	private load() {
		return this._loading ??= (async () => {
			this.aggregation = await rp.ms('definition').get<StatementRowFromRow>(`statementRowAggregates/${this.idRow}`) || { id_row: this.idRow };
			const idRowFrom = this.aggregation.id_row_from || this.idRow;
			this.idStatementFrom = (await rp.ms('definition').get<StatementRow>(`statementRows/${idRowFrom}`))?.id_statement ?? null;
		})();
	}

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			data: 'description',
			t: 't',
			e: false,
			w: 180,
		}, {
			data: 'level_dim',
			header: msg('Level'),
			t: 's',
			e: true,
			w: 75,
			content: [
				{ k: 0, v: msg('All') },
				{ k: 1, v: '1' },
				{ k: 2, v: '2' },
				{ k: 3, v: '3' },
				{ k: 4, v: '4' },
				{ k: 5, v: '5' }
			]
		}, {
			data: 'filter_dim',
			header: `${msg('Filters')} - ${msg('included')}`,
			t: 'm',
			e: true,
			w: 400,
		}, {
			data: 'not_dim',
			header: `${msg('Filters')} - ${msg('excluded')}`,
			t: 'm',
			e: true,
			w: 400,
		}, {
			data: 'dim_num',
			t: 't',
			e: false,
			hidden: true
		},];
		return this._columns;
	}

	async getRows(): Promise<RowSource[]> {
		await this.load();
		const dims = await rp.ms('definition').get<DimensionDefinition[]>(`visibleDimensions/${MODULE_CODES.FINANCE}`)
		const rows = dims.map(data => ({ 
			data: {
				description: data.description,
				level_dim: Boolean(this.aggregation.id_row_from) ? this.aggregation[`level_dim_${data.dim_num}` as keyof StatementRowFromRow] : 0,
				filter_dim: {
					v: this.aggregation[`filter_dim_${data.dim_num}` as keyof StatementRowFromRow],
					dv: this.aggregation[`filter_dim_values_${data.dim_num}` as keyof StatementRowFromRow] || null,
					// dv: (this.aggregation[`filter_dim_${data.dim_num}` as keyof StatementRowFromRow] as string[])?.length > 0
					// 	? `Selected: ${(this.aggregation[`filter_dim_${data.dim_num}` as keyof StatementRowFromRow] as string[]).length} ${msg('item/s')}`
					// 	: null, // use with findById2
					adapter: new DimTreeAdapter(MODULE_CODES.FINANCE, data.dim_num) 
				},
				not_dim: {
					v: this.aggregation[`not_dim_${data.dim_num}` as keyof StatementRowFromRow],
					dv: this.aggregation[`not_dim_values_${data.dim_num}` as keyof StatementRowFromRow] || null,
					// dv: (this.aggregation[`not_dim_${data.dim_num}` as keyof StatementRowFromRow] as string[])?.length > 0
					// 	? `Selected: ${(this.aggregation[`not_dim_${data.dim_num}` as keyof StatementRowFromRow] as string[]).length} ${msg('item/s')}`
					// 	: null, // use with findById2
					adapter: new DimTreeAdapter(MODULE_CODES.FINANCE, data.dim_num)
				},
				dim_num: data.dim_num,
			}
		}));
		return rows;
	}

	async getStatementItems(): Promise<NodeSource[]> {
		const statements = await rp.ms('definition').get<StatementDefinition[]>('statements');
		return statements.map(statement => ({ k: statement.id_statement.toString(), v: statement.statement, props: statement }));
	}

	async getRowItems(): Promise<NodeSource[]> {
		if (!this.idStatementFrom) return [{ k: null, v: msg('Select a report first') }];
		const statementRows = await rp.ms('definition').get<StatementRow[]>('statementRows', { id_statement: this.idStatementFrom });
		// neither a computed (formula) row nor the aggregating row itself can be the source of the aggregation
		return statementRows
			.filter(row => !row.formula && row.id_row !== this.idRow)
			.map(row => ({k: row.id_row.toString(), v: row.row}));
	}

	async getOptions(): Promise<Partial<GridEditorOptions>> {
		await this.load();
		const ddStatementItems = await this.getStatementItems();
		const ddRowItems = await this.getRowItems();
		return this.createOptions({
			colLetters: false,
			rowNumbers: false,
			columnSorting: false,
			filters: false,
			saveMode: 'all_rows',
			refreshAfterSave: true, // reload the rows so the filter cells show the dimension names from the server, not the picked keys
			pageSize: null,
			toolbar: {
				save: {
					text: msg('Save'),
					color: ColorTypes.Green,
					icon: 'save',
					enabled: true
				},
				refresh: {enabled: true},
				discard: {enabled: false},
				new: {enabled: false},
			},
			customToolbar: [{
				type: 'dropdown',
				id: 'ddStatement',
				caption: msg('Source report'),
				width: 250,
				items: ddStatementItems,
				value: this.idStatementFrom?.toString() ?? null,
			}, {
				type: 'dropdown',
				id: 'ddRowFrom',
				caption: msg('Source row'),
				width: 350,
				items: ddRowItems,
				value: this.aggregation.id_row_from?.toString() ?? null,
			}, {
				type: 'dropdown',
				id: 'ddSide',
				caption: msg('Side'),
				width: 150,
				items: [
					{ k: 'T', v: msg('Turnover') },
					{ k: 'D', v: msg('Debit') },
					{ k: 'C', v: msg('Credit') }
				],
				value: this.aggregation.side || 'T',
			}] as ToolbarItem[],
		});
	}

	async save(rowData: RowData[]) {
		if (!this.aggregation.id_row_from) {
			rp.host.bucket.showAlert({content: msg('Row cannot be empty!'), type: 'warning'});
			return Promise.reject('id_row_from cannot be empty');
		}

		for (const { data } of rowData) {
			for (const column of Object.keys(data)) {
				let value;
				switch (column) {
					case 'level_dim':
						value = data[column] || 0;
						break;
					case 'filter_dim':
					case 'not_dim':
						if (!Array.isArray(data[column])) continue;
						const arr = data[column] as any[];
						value = Array.isArray(arr?.[0]) ? arr.map(item => item[0]) : arr;
						if (value.length === 0) value = null;
						break;
					default:
						continue;
				}
				(this.aggregation as any)[`${column}_${data.dim_num}`] = value;
			}
		}
		await rp.ms('definition').post('statementRowAggregates', this.aggregation);
		// the POST response carries no display values (filter_dim_values_*); drop the cached state so the
		// refresh that follows reloads the saved aggregation from the server
		this._loading = null;
	}

	async onAction(action: string, cell: CellModel, event?: ActionEvent<null>) {
		switch (action) {
			case 'ddStatement':	
				this.idStatementFrom = parseInt(event.action.value as string);
				const ddRowFromTree = (this.editor.customToolbar.controls.get('ddRowFrom') as ToolbarDropdown).tree;
				ddRowFromTree.value = this.aggregation.id_row_from = null;
				ddRowFromTree.setSourceData([{ k: null, v: `${msg('Loading')}...` }]);
				const rowsItems = await this.getRowItems();
				ddRowFromTree.setSourceData(rowsItems);
				this.editor.customToolbar.items.find(item => item.id === 'ddRowFrom').items = rowsItems;
				break;
			case 'ddRowFrom':
				if (!event.action.value) return;
				this.aggregation.id_row_from = parseInt(event.action.value as string);
				break;
			case 'ddSide':
				this.aggregation.side = (event.action.value == 'T' ? null : event.action.value) as string;
				break;
		}
	}

}
